import React from 'react';
import { useEffect, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AnimatePresence } from 'framer-motion';
import LogoTwygs from '../../../assets/logo-twygs.svg';
import AvatarIcon from '../../../assets/medium_default_optimized.png';
import WriteIcon from '../../../assets/write-icon.svg';
import WriteGreenIcon from '../../../assets/write-green-icon.svg';
import MenuIcon from '../../../assets/menu-icon.svg';
import CloseIcon from '../../../assets/close-black-icon.svg';
import { useClickOutside } from '../../../hooks/useOutsideClick';
import { profileAtom } from '../../Settings/atom';
import HeaderActionComponent from './header-action.component';
import NotificationComponent from '../Notification/notification.component';
import { getUserInfo } from '../../../apis/discovery.api';
import { openSideBarAtom } from './atom';
import MenuDropdown from '../Menu';
import LogOutIcon from '../../../assets/logout-icon.svg';
import ProfileIcon from '../../../assets/profile-black-icon.svg';
import SettingIcon from '../../../assets/setting-icon.svg';
import DashboardIcon from '../../../assets/dashboard-icon.svg';
import TwygMobileLogo from '../../../assets/twygs-mobile-logo.svg';
import InvestIcon from '../../../assets/invest-black-icon.svg';
import SwitchIcon from '../../../assets/switch-icon.svg';
import { removeAllCookies } from '../../../constants';
import MyRankComponent from './MyRank';
import FeedSettingsModal from '../../CustomFeeds';
import { Sliders } from 'lucide-react';
import LeftContentComponent from '../../DiscoveryComponent/LeftContentComponent';

const HeaderComponent = () => {
  const [profile, setProfile] = useAtom(profileAtom);
  const [open, setOpen] = useState(false);
  const [openSidebar, setOpenSidebar] = useAtom(openSideBarAtom);
  const [isFeedSettingsOpen, setIsFeedSettingsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const listItem = [
    {
      name: 'Switch Admin',
      icon: SwitchIcon,
      onClick: () => {
        window.location.href = `/dashboard/home`;
      },
      isHidden: !profile.isAdmin
    },
    {
      name: 'View Profile',
      icon: ProfileIcon,
      onClick: () => {
        navigate(`/${profile?.username}`);
      }
    },
    {
      name: 'Dashboard',
      icon: DashboardIcon,
      onClick: () => {
        navigate(`/article/manage?tab=${profile.isFoundingCreator ? 'articles' : 'awarded-twigs'}`);
      }
    },
    {
      name: 'Earnings',
      icon: InvestIcon,
      onClick: () => {
        window.location.href = `/creator-dashboard/home`;
      }
    },
    {
      name: 'Learning',
      icon: () => (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
          />
        </svg>
      ),
      onClick: () => {
        navigate('/learning');
        setOpen(false);
      }
    },
    {
      name: 'Feed Settings',
      icon: Sliders,
      onClick: () => {
        setIsFeedSettingsOpen(true);
        setOpen(false);
      }
    },
    {
      name: 'Account Settings',
      icon: SettingIcon,
      onClick: () => {
        navigate('/account-settings');
      }
    },
    {
      name: 'Log Out',
      icon: LogOutIcon,
      onClick: () => {
        removeAllCookies();
        setProfile({});
        navigate(0);
      }
    }
  ];

  const token = Cookies.get(`token`);

  useClickOutside(dropdownRef, () => {
    setOpen(false);
  });

  const handleToggleProfileModal = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const result = await getUserInfo();
        if (result) {
          setProfile(result);
          localStorage.setItem('user', JSON.stringify(result));
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
      }
    };
    if (token) fetchUser();
  }, [token]);

  return (
    <>
      {/* Mobile sidebar backdrop - rendered outside header z-index */}
      <AnimatePresence>{openSidebar && <LeftContentComponent isFixed />}</AnimatePresence>
      <div className="fixed top-0 left-0 w-full z-[100]" id="writer-header">
        <div className="max-w-[1920px] mx-auto bg-white w-full md:border-b-[2px] border-solid border-04-anti-flash-white">
          <div className="m-auto px-4 md:px-[40px] 2xl:px-[60px] w-full py-4 md:py-5 flex items-center">
            <div className="flex items-center">
              <img
                src={LogoTwygs}
                alt="logo"
                className="h-8 cursor-pointer hidden lg:!block"
                onClick={() => {
                  navigate('/');
                  window.scrollTo(0, 0);
                }}
              />
              <img
                src={TwygMobileLogo}
                alt="logo"
                className="cursor-pointer lg:hidden mr-2"
                onClick={() => {
                  navigate('/');
                  window.scrollTo(0, 0);
                }}
              />
              <img
                src={openSidebar ? CloseIcon : MenuIcon}
                className="block lg:!hidden cursor-pointer"
                alt=""
                onClick={() => setOpenSidebar((prev) => !prev)}
              />
              <p
                className="text-xl ml-2 font-extrabold hidden lg:!block tracking-tighter text-growth-green-500 cursor-pointer"
                onClick={() => {
                  navigate('/');
                  window.scrollTo(0, 0);
                }}
              >
                Beta
              </p>
            </div>

            <div className="flex-1 hidden lg:!block relative">
              <HeaderActionComponent />
            </div>
            {token && profile ? (
              <div className="flex items-center gap-2 md:gap-[20px] relative ml-auto">
                <MyRankComponent
                  pointName={'Twigs'}
                  pointsBalance={profile?.dailyPointsRemaining}
                  profile={profile}
                />
                {profile?.isCreator && (
                  <>
                    <div
                      onClick={() => navigate('/article/create')}
                      className="cursor-pointer hidden md:!flex items-center gap-2 h-9 bg-growth-green px-[14px] py-[6px] text-center !rounded-[8px] !text-white text-sm font-[700] hover:opacity-80"
                    >
                      Write
                      <img src={WriteIcon} className="w-5 h-5" alt="write" />
                    </div>
                    <div
                      onClick={() => navigate('/article/create')}
                      className="bg-05-seasalt-white flex md:hidden rounded-full w-9 h-9 md:h-10 md:w-10 items-center justify-center cursor-pointer"
                    >
                      <img src={WriteGreenIcon} alt="" />
                    </div>
                  </>
                )}
                <NotificationComponent />
                <div ref={dropdownRef}>
                  <img
                    onClick={handleToggleProfileModal}
                    src={profile?.imgUrl || AvatarIcon}
                    alt="avatar"
                    className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-solid border-04-anti-flash-white cursor-pointer object-cover hover:opacity-80"
                  />
                  <div
                    className={`${
                      open ? 'flex' : 'hidden'
                    } absolute z-20 top-[54px] right-0 p-2 flex flex-col gap-[8px] bg-white w-[240px] rounded-[12px] border border-solid border-[#F2F3F7] shadow-[0px_8px_12px_0px_#80808026]`}
                  >
                    <MenuDropdown
                      open={open}
                      setOpen={setOpen}
                      listItem={listItem}
                      profile={profile}
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4 ml-auto">
                <a
                  href={`/authen/signup?from=${window.location.href}`}
                  target="_self"
                  className="px-[14px] py-[6px] text-growth-green-500 cursor-pointer border-[2px] border-growth-green-500 rounded-[8px] text-sm font-black whitespace-nowrap hover:opacity-80"
                >
                  Sign Up
                </a>
                <a
                  href={`/authen/login?from=${window.location.href}`}
                  target="_self"
                  className="cursor-pointer bg-growth-green px-[14px] py-[8px] text-center !rounded-[8px] text-white text-sm font-black hover:opacity-80"
                >
                  Log In
                </a>
              </div>
            )}
          </div>
        </div>
        <FeedSettingsModal open={isFeedSettingsOpen} onOpenChange={setIsFeedSettingsOpen} />
      </div>
    </>
  );
};

export default HeaderComponent;
